import React, { useState } from "react";
import { Form as AntForm } from "antd";
import { withFormik, FormikProps, Field, Form } from "formik";
import { memberIdSchema, validUserSchema } from "@njoy/common";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../../shared/InputField";
import { Link } from "react-router-dom";
import { NormalizedErrorMap } from "@njoy/controller";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LoginModal } from "../../../../components/modals/loginModal";
import NewButton from "../../../../components/uiMerge/newButton";
import { BrandLogo } from "../../../../components/shared/BrandLogo";
import { PhoneInputField } from "../../../../modules/shared/PhoneInputField";
import * as ROUTES from "../../../../constants/routes";
import { CheckboxField } from "src/modules/shared/CheckboxField";
import ToastMessage from "../../../../hooks/useToast";
import { extractError } from "../../../../utils/extractError";

const FormItem = AntForm.Item;
interface FormValues {
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  nda: boolean;
  memberId: string;
}

interface Props {
  onFinish: (email: string) => void;
  submit: (values: Omit<FormValues, "memberId"> & { memberId?: string }) => Promise<NormalizedErrorMap | null>;
  // Members-only sites (e.g. CBM) need a verified member ID to sign up.
  requiresMemberId: boolean;
}

const C: React.FC<FormikProps<FormValues> & Props> = (props) => {
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const countries = [
    { code: "NG", label: "+234" },
    { code: "US", label: "+1" },
    { code: "UK", label: "+44" },
    { code: "CA", label: "+1" },
  ];

  return (
    <div className="flex flex-col min-h-[98vh] w-full mt-5 py-[2rem] px-[1rem]">
      <React.Fragment>
        <ToastContainer />

        <div className="flex flex-row justify-center items-center ">
          <h2 className=" flex flex-row text-white text-2xl items-center justify-center font-bold">
            Sign up to{" "}
            <span className="px-2">
              <Link className="cursor-pointer" to={"/"}>
                <BrandLogo className="w-[66px]" />
              </Link>
            </span>
          </h2>
        </div>

        <p className="text-sm mt-2 text-center text-white/60">
          Make life easier with exclusive verified perks.
        </p>

        <div className="mt-8 mb-2 w-full flex-1 flex flex-col">
          <p className="text-white !font-[900]">Account Information</p>
          <br />

          <Form className="flex flex-col flex-1">
            <div className="w-full flex-1 flex flex-col">
              <div className="flex-1">
                <div className="flex-1">
                  <label
                    className="text-[#CECECF] opacity-90"
                    htmlFor="Email Address"
                  >
                    {" "}
                    Email Address
                  </label>
                  <Field
                    name="email"
                    placeholder="Email Address"
                    component={InputField}
                  />

                  <label className="text-[#CECECF] opacity-90">
                    {" "}
                    Phone Number
                  </label>
                  <Field
                    name="phone"
                    placeholder="Phone Number"
                    component={PhoneInputField}
                    countries={countries}
                  />

                  {props.requiresMemberId && (
                    <>
                      <label className="text-[#CECECF] opacity-90" htmlFor="memberId">
                        CBM Member ID
                      </label>
                      <Field
                        name="memberId"
                        placeholder="Your CBM member ID"
                        component={InputField}
                      />
                    </>
                  )}

                  <label
                    className="text-[#CECECF] opacity-90"
                    htmlFor="password"
                  >
                    {" "}
                    Create Password
                  </label>
                  <Field
                    name="password"
                    type="password"
                    placeholder="Password"
                    component={InputField}
                    className={"focus:ring-2"}
                  />

                  <label
                    className="text-[#CECECF] opacity-90"
                    htmlFor="confirmPassword"
                  >
                    Confirm Password
                  </label>
                  <Field
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    component={InputField}
                    className="focus:ring-4"
                  />

                  <Field
                    name="nda"
                    component={CheckboxField}
                    label={
                      <>
                        I agree to njoy's{" "}
                        <span
                          className="text-purple-700 "
                          // href="https://njoy.live/terms-conditions"
                          // target="_blank"
                          // rel="noopener noreferrer"
                          onClick={() => navigate(ROUTES.TERMS_CONDITIONS)}
                        >
                          Terms & Conditions
                        </span>{" "}
                      </>
                    }
                  />

                  <div className="mt-auto pt-3">
                    <FormItem>
                      <NewButton
                        loading={props.isSubmitting}
                        fullWidth
                        variant="primary"
                        type="submit"
                      >
                        Continue
                      </NewButton>
                    </FormItem>

                    <FormItem>
                      <div className="flex flex-row w-full justify-center text-white">
                        Already have an account?
                        <Link
                          to={ROUTES.LOGIN}
                          className="text-[color:var(--brand-primary)] cursor-pointer"
                        >
                          &nbsp;Sign in
                        </Link>
                      </div>
                    </FormItem>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </React.Fragment>

      <LoginModal visible={loginOpen} onCancel={() => setLoginOpen(false)} />
    </div>
  );
};

export const RegisterView = withFormik<Props, FormValues>({
  validationSchema: (props: Props) =>
    props.requiresMemberId
      ? validUserSchema.shape({ memberId: memberIdSchema.required("Enter your CBM member ID") })
      : validUserSchema,
  mapPropsToValues: () => ({
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    nda: false,
    memberId: "",
  }),
  handleSubmit: async (values, { props, setErrors, setSubmitting, setTouched }) => {
    try {
      setSubmitting(true);
      const { memberId, ...account } = values;
      const serverErrors = await props.submit(
        props.requiresMemberId ? { ...account, memberId: memberId.trim() } : account
      );

      if (serverErrors) {
        setSubmitting(false);
        ToastMessage("error", extractError(serverErrors));
        setErrors(serverErrors);
        setTouched(
          Object.keys(serverErrors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {} as Record<string, boolean>
          )
        );
        return;
      }

      setSubmitting(false);
      props.onFinish(values.email);
    } catch (error: any) {
      setSubmitting(false);
      ToastMessage("error", (error as Error)?.message);
    }
  },
})(C as any);
